import { TrainingType } from "../../../types/activity";
import { CustomTrainingType } from "../../../types/user";
import { trainingTypeService } from "../../../services/trainingTypeService";

export type { TrainingType };

// ── Calculations (identical to the original training log form) ───────────────

// rpe = (muscular + respiratory) / 2
export const calcRpe = (muscular: number, respiratory: number): number =>
  (muscular + respiratory) / 2;

// Parse a duration string that may be "H:MM" or plain minutes into minutes.
export const parseDurationToMinutes = (duration: string): number => {
  if (duration.includes(":")) {
    const [h, m = "0"] = duration.split(":");
    const hours = Number(h);
    const minutes = Number(m);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return hours * 60 + minutes;
    }
    return 0;
  }
  const val = parseInt(duration, 10);
  return Number.isFinite(val) ? val : 0;
};

// The selected training type's modifier (1 when none selected).
export const calcLoadModifier = (
  selectedTrainingType: TrainingType | null,
): number => selectedTrainingType?.modifier ?? 1;

// load = round(rpe * durationInMinutes * loadModifier)
export const calcLoad = (
  rpe: number,
  durationInMinutes: number,
  loadModifier: number,
): number => Math.round(rpe * durationInMinutes * loadModifier);

// Convert dateID (YYYYMMDD) to YYYY-MM-DD.
export const convertDateIDToYYYYMMDD = (dateID: string): string => {
  const year = dateID.substring(0, 4);
  const month = dateID.substring(4, 6);
  const day = dateID.substring(6, 8);
  return `${year}-${month}-${day}`;
};

// ── Training type fetching / resolution ──────────────────────────────────────

// Fetch the training types available to the athlete based on their selected
// sports (falls back to the full catalogue when no sports are selected), then
// append the athlete's custom training types.
export const fetchTrainingTypesForUser = async (
  trainingSportIDs: string[] | undefined,
  customTrainingTypes?: CustomTrainingType[],
): Promise<TrainingType[]> =>
  trainingTypeService.fetchTrainingTypesForSports(
    trainingSportIDs,
    customTrainingTypes,
  );

// Display / grouping names for a log: prefer the canonical training type name
// (via trainingTypeID), falling back to the stored legacy tags.
export const getLogTrainingTypeNames = (
  log: { trainingTypeID?: string; tag?: string[] },
  trainingTypeNamesById: Record<string, string>,
): string[] => {
  if (log.trainingTypeID && trainingTypeNamesById[log.trainingTypeID]) {
    return [trainingTypeNamesById[log.trainingTypeID]];
  }
  return log.tag && log.tag.length > 0 ? log.tag : [];
};

// Resolve the training type behind an existing log: prefer trainingTypeID,
// then fall back to matching legacy tags against names / alternativeNames.
export const resolveTrainingTypeForLog = (
  trainingTypes: TrainingType[],
  trainingTypeID: string | undefined,
  tags: string[] | undefined,
): TrainingType | null => {
  if (trainingTypeID) {
    const byId = trainingTypes.find((t) => t.id === trainingTypeID);
    if (byId) return byId;
  }
  for (const tag of tags || []) {
    const normalized = tag.trim().toLowerCase();
    const byName = trainingTypes.find(
      (t) =>
        t.name.trim().toLowerCase() === normalized ||
        t.alternativeNames?.some(
          (alt) => alt.trim().toLowerCase() === normalized,
        ),
    );
    if (byName) return byName;
  }
  return null;
};
